import clsx from 'clsx';
import { Dialog as RadixDialog } from 'radix-ui';
import { X } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';

/* -------------------------------------------------------------------------
   Dialog

   Radix rather than a hand-rolled modal. Focus trapping, focus restoration,
   inert background content, Escape handling and scroll locking are each easy to
   get slightly wrong and impossible to notice with a mouse — a modal that leaks
   focus to the page behind it is unusable with a keyboard and perfectly fine in
   a screenshot.
   ------------------------------------------------------------------------- */

/**
 * Surfaces that appear over the page and take focus with them.
 *
 * Anything focused inside one of these is a transient position, not somewhere
 * to return to: the surface will be gone by the time it matters.
 */
const TRANSIENT_LAYERS = '[role="dialog"],[role="menu"],[role="listbox"],[role="alertdialog"]';

/**
 * The last element focused outside every transient layer, tracked for the whole
 * document rather than per dialog.
 *
 * It began as state inside `Dialog`, registered by an effect on mount. That
 * works only for a dialog that is already mounted when the operator focuses the
 * control that opens it. A dialog rendered on demand — `{editing !== null ? …}`,
 * which is how a table's row actions work — mounts at the moment the menu item
 * is chosen, so its listener started AFTER the row's menu button had been
 * focused, and it had nothing recorded. Focus went to `document.body`.
 *
 * Where focus last was is a fact about the document, not about any one dialog.
 * One listener, installed the first time any dialog is constructed, knows it
 * regardless of what mounts when.
 */
let lastExternalFocus: HTMLElement | null = null;
let tracking = false;

function trackFocus(): void {
  if (tracking || typeof document === 'undefined') return;
  tracking = true;
  document.addEventListener(
    'focusin',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.closest(TRANSIENT_LAYERS) !== null) return;
      lastExternalFocus = target;
    },
    // Capture phase: focusin bubbles, but this must see it before any handler
    // that might stop propagation.
    true,
  );
}

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Announced with the title. Say what the action will do, not what it is called. */
  description?: string;
  children: ReactNode;
  /** Buttons. Primary action last, matching the platform convention. */
  footer?: ReactNode;
  className?: string;
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  className,
}: DialogProps) {
  /*
    Where focus must go when this closes.

    Radix's modal content cancels the browser's own focus restoration and
    focuses `Dialog.Trigger` instead. These dialogs are controlled by state
    rather than opened by a `Trigger`, so there is no trigger for it to focus
    and focus lands on `document.body` — invisible with a mouse, and for a
    keyboard user it throws away their position on the page every time a dialog
    closes.

    The answer comes from the document-level tracker above rather than from
    anything this component observed, because a dialog that mounts on demand has
    not been watching long enough to know.
  */
  useEffect(trackFocus, []);

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-black/45 backdrop-blur-[1px]" />
        <RadixDialog.Content
          onCloseAutoFocus={(event) => {
            const target = lastExternalFocus;
            // Only take over when the element is still on the page; otherwise
            // leave Radix to its default rather than focusing a detached node.
            if (!target || !target.isConnected) return;
            event.preventDefault();
            target.focus();
          }}
          /*
            The height cap and the scrolling body are load-bearing, not polish.

            A centred `position: fixed` surface with no maximum height grows past
            both edges of the viewport once its form is long enough, and Radix
            locks the page behind it, so there is nothing left to scroll: the
            footer — Cancel and the primary action — sits below the fold and
            cannot be reached by mouse OR by keyboard. Tab still moves focus into
            it, which is worse than a dead control, because the focus ring is
            off-screen and the operator cannot see where they are.

            This is reached by ordinary use rather than by an extreme window: the
            plan editor grows a Term field the moment a licence type is chosen,
            and that alone pushed it past a 1032px viewport.
          */
          className={clsx(
            'fixed left-1/2 top-1/2 z-50 w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2',
            'flex max-h-[calc(100dvh-2rem)] flex-col',
            'rounded-[var(--vc-radius-lg)] border border-[var(--vc-border)] bg-[var(--vc-surface)] shadow-xl',
            'focus:outline-none',
            className,
          )}
        >
          <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[var(--vc-border)] px-5 py-4">
            <div>
              <RadixDialog.Title className="text-[15px] font-semibold text-[var(--vc-text)]">
                {title}
              </RadixDialog.Title>
              {description ? (
                <RadixDialog.Description className="mt-1 text-[12.5px] text-[var(--vc-text-subtle)]">
                  {description}
                </RadixDialog.Description>
              ) : null}
            </div>

            <RadixDialog.Close
              className={
                'rounded-[var(--vc-radius-sm)] p-1 text-[var(--vc-text-muted)] ' +
                'transition-colors duration-[var(--vc-duration-fast)] hover:bg-[var(--vc-surface-hover)] ' +
                'hover:text-[var(--vc-text)] focus-visible:outline-2 focus-visible:outline-offset-2 ' +
                'focus-visible:outline-[var(--vc-focus-ring)]'
              }
              aria-label="Close"
            >
              <X aria-hidden="true" className="size-4" />
            </RadixDialog.Close>
          </div>

          {/* `overflow-y: auto` is what lets this shrink inside the flex column:
              a flex item that scrolls has an automatic minimum size of zero, so
              no explicit `min-h-0` is needed — and adding one was verified to
              change nothing. */}
          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

          {footer ? (
            <div className="flex shrink-0 justify-end gap-2 border-t border-[var(--vc-border)] px-5 py-3.5">
              {footer}
            </div>
          ) : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
